import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axiosConfig';
import { AuthContext } from '../context/AuthContext';
import * as XLSX from 'xlsx';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import './Dashboard.css';

const CobroEjecutados = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [stats, setStats] = useState({ total_ejecutados: 0, pendientes: 0, cobrados: 0, pagados: 0 });
  const [tramites, setTramites] = useState([]);
  const [concesionarios, setConcesionarios] = useState([]);
  const [transitos, setTransitos] = useState([]);

  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');

  // Filtros - Estado por defecto: Pendientes
  const [filterEstado, setFilterEstado] = useState('pendiente');
  const [filterConcesionario, setFilterConcesionario] = useState('');
  const [filterTransito, setFilterTransito] = useState('');
  const [search, setSearch] = useState('');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');

  // Selección múltiple
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    if (user && user.role !== 'superadmin' && user.profile_name !== 'superadmin') {
      navigate('/dashboard');
      return;
    }
    fetchAuxiliaryData();
    fetchStats();
  }, [user, navigate]);

  useEffect(() => {
    fetchTramites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterEstado, filterConcesionario, filterTransito, search, fechaInicio, fechaFin]);

  const fetchAuxiliaryData = async () => {
    try {
      const [resCon, resTra] = await Promise.all([
        axios.get('/api/concesionarios').catch(() => ({ data: [] })),
        axios.get('/api/transitos').catch(() => ({ data: [] }))
      ]);
      setConcesionarios(resCon.data || []);
      setTransitos(resTra.data || []);
    } catch (err) {
      console.error('Error al cargar datos auxiliares:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get('/api/cobro-ejecutados/stats');
      setStats(res.data || { total_ejecutados: 0, pendientes: 0, cobrados: 0, pagados: 0 });
    } catch (err) {
      console.error('Error al obtener estadísticas de cobros ejecutados:', err);
    }
  };

  const fetchTramites = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        liquidacion_estado: filterEstado,
        concesionario_id: filterConcesionario,
        transito_id: filterTransito,
        search,
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin
      }).toString();

      const res = await axios.get(`/api/cobro-ejecutados?${params}`);
      setTramites(res.data || []);
      setSelectedIds([]);
      setError('');
    } catch (err) {
      console.error('Error al consultar trámites ejecutados:', err);
      setError('Error al cargar la lista de trámites ejecutados');
    } finally {
      setLoading(false);
    }
  };

  // Selección
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(tramites.map(t => t.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(item => item !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  // Cambio de estado
  const handleUpdateStatus = async (nuevoEstado) => {
    if (selectedIds.length === 0) {
      alert('Por favor selecciona al menos un trámite para marcarlo como pagado.');
      return;
    }

    const confirmMsg = nuevoEstado === 'pagado'
      ? `¿Estás seguro de marcar los ${selectedIds.length} trámite(s) seleccionados como PAGADOS?`
      : `¿Estás seguro de cambiar el estado de ${selectedIds.length} trámite(s) a ${nuevoEstado.toUpperCase()}?`;

    if (!window.confirm(confirmMsg)) return;

    try {
      setUpdating(true);
      await axios.put('/api/cobro-ejecutados/update-status', {
        tramite_ids: selectedIds,
        nuevo_estado: nuevoEstado
      });

      alert(`✅ ${selectedIds.length} trámite(s) actualizados a ${nuevoEstado.toUpperCase()} exitosamente.`);
      await fetchStats();
      await fetchTramites();
    } catch (err) {
      console.error(err);
      alert('❌ Error al actualizar el estado de pago: ' + (err.response?.data?.message || err.message));
    } finally {
      setUpdating(false);
    }
  };

  // Helpers de formateo
  const formatDateShort = (dateStr) => {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getTramiteDetails = (t) => {
    const datos = t.datos_adicionales || {};
    const v = datos.vehicleDetails?.vehiculo || {};

    const propietario = datos.propietario || datos.nombre_propietario || 'N/A';
    const documento = datos.documento || datos.identificacion || 'N/A';
    const marca = datos.marca || v.marca || 'N/A';
    const linea = datos.linea || v.linea || 'N/A';

    const rawLiq = datos.total_liquidacion || datos.valor_liquidacion || datos.liquidacion_total || datos.total_liq || datos.valor_liq_transito || 0;
    const valorLiquidacionTransito = parseFloat(rawLiq) || 0;

    const rawCupl = datos.cupl_total || datos.valor_cupl || datos.cupl_valor || datos.cupl_val || datos.monto_cupl || 0;
    const valorCupl = parseFloat(rawCupl) || 0;

    const rawImp = t.impuesto_valor || datos.impuesto_valor || datos.valor_impuesto || datos.impuesto_monto || 0;
    const valorImpuesto = parseFloat(rawImp) || 0;

    const rawSoat = t.soat_monto || datos.valor_soat || datos.soat_info?.valor_soat || datos.soat_valor || datos.monto_soat || 0;
    const valorSoat = parseFloat(String(rawSoat).replace(/[^0-9.]/g, '')) || 0;

    return { 
      propietario, 
      documento, 
      marca, 
      linea,
      valorLiquidacionTransito,
      valorCupl,
      valorImpuesto,
      valorSoat
    };
  };

  // Exportar Excel
  const handleExportExcel = () => {
    if (tramites.length === 0) {
      alert('No hay datos disponibles para exportar.');
      return;
    }

    const isSuperAdmin = user?.role === 'superadmin' || user?.profile_name === 'superadmin';

    const dataToExport = tramites.map(t => {
      const details = getTramiteDetails(t);
      const row = {
        'Número de Trámite': t.id,
        'Placa': t.plate || 'Sin Placa',
        'Propietario': details.propietario,
        'Cédula / Documento': details.documento,
        'Tipo de Trámite': t.tipo_tramite,
        'Concesionario': t.concesionario_name || 'N/A',
        'Organismo de Tránsito': t.transito_name || 'N/A',
        'Estado del Trámite': 'EJECUTADO',
        'Estado de Pago': (t.liquidacion_estado || 'pendiente').toUpperCase(),
        'Fecha de Ejecución': formatDateShort(t.updated_at),
        'Fecha de Pago': t.liquidacion_fecha_pago ? formatDateShort(t.liquidacion_fecha_pago) : 'N/A'
      };

      if (isSuperAdmin) {
        row['Valor Liquidación Tránsito'] = details.valorLiquidacionTransito;
        row['Valor CUPL'] = details.valorCupl;
        row['Valor Impuesto'] = details.valorImpuesto;
        row['Valor SOAT'] = details.valorSoat;
      }

      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Cobros Ejecutados');
    XLSX.writeFile(workbook, `Cobros_Ejecutados_${formatDateShort(new Date()).replace(/\//g, '-')}.xlsx`);
  };

  return (
    <div className="app-layout">
      <Sidebar />
      <div className="main-content">
        <Navbar title="💰 Cobros Ejecutados (Superadmin)" />
        <div className="page-content">
          {error && <div className="error-banner">{error}</div>}

          {/* Tarjetas KPI */}
          <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', marginBottom: '20px' }}>
            <div className="stat-card" style={{ borderLeft: '4px solid #f59e0b' }}>
              <div className="stat-details">
                <span className="stat-label">Pendientes de Pago</span>
                <span className="stat-value" style={{ color: '#d97706' }}>{stats.pendientes}</span>
              </div>
            </div>

            <div className="stat-card" style={{ borderLeft: '4px solid #10b981' }}>
              <div className="stat-details">
                <span className="stat-label">Marcados como Pagados</span>
                <span className="stat-value" style={{ color: '#059669' }}>{stats.pagados}</span>
              </div>
            </div>

            <div className="stat-card" style={{ borderLeft: '4px solid #3b82f6' }}>
              <div className="stat-details">
                <span className="stat-label">Total Trámites Ejecutados</span>
                <span className="stat-value" style={{ color: '#1e293b' }}>{stats.total_ejecutados}</span>
              </div>
            </div>
          </div>

          {/* Filtros y Selección */}
          <div className="dashboard-controls" style={{ background: '#fff', padding: '18px 20px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', marginBottom: '20px' }}>
            <div className="quick-filters" style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
              <button
                className={`btn-filter ${filterEstado === 'pendiente' ? 'active' : ''}`}
                onClick={() => setFilterEstado('pendiente')}
                style={filterEstado === 'pendiente' ? { background: '#fef3c7', color: '#b45309', border: '1px solid #f59e0b' } : {}}
              >
                ⏳ Pendientes de Pago ({stats.pendientes})
              </button>
              <button
                className={`btn-filter ${filterEstado === 'pagado' ? 'active' : ''}`}
                onClick={() => setFilterEstado('pagado')}
                style={filterEstado === 'pagado' ? { background: '#ecfdf5', color: '#047857', border: '1px solid #10b981' } : {}}
              >
                ✅ Pagados ({stats.pagados})
              </button>
              <button
                className={`btn-filter ${filterEstado === 'todos' ? 'active' : ''}`}
                onClick={() => setFilterEstado('todos')}
              >
                Todos ({stats.total_ejecutados})
              </button>
            </div>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 200px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase' }}>Búsqueda</label>
                <input
                  type="text"
                  placeholder="🔍 Número Trámite, Placa, Propietario, Cédula..."
                  className="form-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div style={{ width: '180px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase' }}>Concesionario</label>
                <select
                  className="form-input"
                  value={filterConcesionario}
                  onChange={(e) => setFilterConcesionario(e.target.value)}
                >
                  <option value="">Todos los Concesionarios</option>
                  {concesionarios.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div style={{ width: '180px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase' }}>Tránsito</label>
                <select
                  className="form-input"
                  value={filterTransito}
                  onChange={(e) => setFilterTransito(e.target.value)}
                >
                  <option value="">Todos los Tránsitos</option>
                  {transitos.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>

              <div style={{ width: '130px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase' }}>Desde</label>
                <input
                  type="date"
                  className="form-input"
                  value={fechaInicio}
                  onChange={(e) => setFechaInicio(e.target.value)}
                />
              </div>

              <div style={{ width: '130px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase' }}>Hasta</label>
                <input
                  type="date"
                  className="form-input"
                  value={fechaFin}
                  onChange={(e) => setFechaFin(e.target.value)}
                />
              </div>

              <button
                className="btn-secondary"
                onClick={() => {
                  setSearch('');
                  setFilterConcesionario('');
                  setFilterTransito('');
                  setFechaInicio('');
                  setFechaFin('');
                }}
              >
                Limpiar
              </button>
            </div>
          </div>

          {/* Barra de Acciones y Botón Marcar Pagados */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <span style={{ fontSize: '14px', color: '#475569', fontWeight: '600' }}>
              {selectedIds.length} trámite(s) seleccionado(s)
            </span>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                className="btn-secondary"
                onClick={handleExportExcel}
                style={{ background: '#10b981', color: 'white', borderColor: '#059669' }}
              >
                📥 Exportar Excel
              </button>

              <button
                className="btn-primary"
                onClick={() => handleUpdateStatus('pagado')}
                disabled={selectedIds.length === 0 || updating}
                style={{
                  background: '#059669',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '700',
                  boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.3)',
                  opacity: selectedIds.length === 0 ? 0.5 : 1,
                  cursor: selectedIds.length === 0 ? 'not-allowed' : 'pointer'
                }}
              >
                💰 Marcar Seleccionados como Pagados ({selectedIds.length})
              </button>

              {filterEstado === 'pagado' && (
                <button
                  className="btn-secondary"
                  onClick={() => handleUpdateStatus('pendiente')}
                  disabled={selectedIds.length === 0 || updating}
                >
                  🔄 Revertir a Pendiente
                </button>
              )}
            </div>
          </div>

          {/* Tabla de Trámites Ejecutados */}
          {loading ? (
            <p className="loading">Cargando trámites...</p>
          ) : (
            <div className="table-wrapper">
              <div className="table-responsive">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th style={{ width: '40px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          onChange={handleSelectAll}
                          checked={tramites.length > 0 && selectedIds.length === tramites.length}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                      </th>
                      <th>Número de Trámite</th>
                      <th>Placa</th>
                      <th>Propietario / Cédula</th>
                      <th>Tipo de Trámite</th>
                      <th>Concesionario / Tránsito</th>
                      <th>Estado Trámite</th>
                      <th>Estado de Pago</th>
                      <th>Fecha Ejecución</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tramites.map(t => {
                      const details = getTramiteDetails(t);
                      const isPagado = t.liquidacion_estado === 'pagado';
                      return (
                        <tr key={t.id} className={selectedIds.includes(t.id) ? 'selected-row' : ''}>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(t.id)}
                              onChange={() => handleSelectOne(t.id)}
                              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                          </td>
                          <td>
                            <strong style={{ fontSize: '15px', color: '#1e293b' }}>#{t.id}</strong>
                          </td>
                          <td>
                            <span className="plate-badge-sm" style={{ background: '#f1f5f9', color: '#0f172a', border: '1px solid #cbd5e1', fontWeight: '800', fontSize: '14px' }}>
                              {t.plate || 'Sin Placa'}
                            </span>
                          </td>
                          <td>
                            <strong>{details.propietario}</strong>
                            <div style={{ fontSize: '11px', color: '#64748b' }}>CC: {details.documento}</div>
                          </td>
                          <td>
                            <strong>{t.tipo_tramite}</strong>
                          </td>
                          <td>
                            <div>🏢 {t.concesionario_name || 'N/A'}</div>
                            <div style={{ fontSize: '11px', color: '#64748b' }}>🏛️ {t.transito_name || 'N/A'}</div>
                          </td>
                          <td>
                            <span style={{
                              padding: '5px 10px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: '700',
                              backgroundColor: '#dbeafe',
                              color: '#1e40af',
                              border: '1px solid #bfdbfe',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              ☑️ EJECUTADO
                            </span>
                          </td>
                          <td>
                            <span style={{
                              padding: '6px 12px',
                              borderRadius: '20px',
                              fontSize: '12px',
                              fontWeight: '700',
                              backgroundColor: isPagado ? '#d1fae5' : '#fef3c7',
                              color: isPagado ? '#065f46' : '#92400e',
                              border: isPagado ? '1px solid #a7f3d0' : '1px solid #fde68a',
                              display: 'inline-block'
                            }}>
                              {isPagado ? '✅ PAGADO' : '⏳ PENDIENTE DE PAGO'}
                            </span>
                          </td>
                          <td style={{ fontSize: '12px' }}>
                            <div>{formatDateShort(t.fecha_ejecucion || t.updated_at)}</div>
                            {t.liquidacion_fecha_pago && (
                              <div style={{ color: '#10b981', fontSize: '11px' }}>Pagado: {formatDateShort(t.liquidacion_fecha_pago)}</div>
                            )}
                          </td>
                        </tr>
                      );
                    })}

                    {tramites.length === 0 && (
                      <tr>
                        <td colSpan="9" className="no-data" style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>
                          No hay trámites ejecutados disponibles en esta sección.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CobroEjecutados;
